/**
 * Investigations — durable diagnostic investigation cases (ADR-082 Phase 5).
 *
 * A `fleet-diagnostician` run that hits an evidence gap opens a durable case
 * instead of dead-ending: it proposes acquiring the missing evidence, sleeps
 * across deploys/restarts, and resumes on the next captured failure. This
 * view lists every case for the fleet, filterable by status; clicking one
 * opens the detail view (InvestigationDetail.tsx) with the steps-tried
 * timeline and, for an awaiting-approval case, the inline acquisition
 * approval control.
 *
 * Mirrors Improvements.tsx's shape (status-filter tabs + poll) rather than
 * Approvals.tsx's single show-all toggle, since investigations have six
 * lifecycle statuses worth filtering independently.
 *
 * Polls GET /api/investigations every 15s. The api client handles silent
 * token refresh; an UnauthorizedError here means the console session is
 * truly gone.
 */
import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api, UnauthorizedError } from '../api/client'
import type { DiagnosticInvestigation, InvestigationStep } from '../api/client'
import { relativeTime } from '../utils/relativeTime'
import { statusBadgeClass } from '../utils/investigationStatus'
// ADR-148 D2 — the waiting wording and the operator-owned artifacts live in utils so this
// page and the detail view cannot describe the same case differently.
import { waitingOn, operatorArtifacts } from '../utils/capabilityGap'

const POLL_INTERVAL_MS = 15_000

const STATUS_FILTERS = ['all', 'active', 'awaiting-approval', 'awaiting-evidence', 'resolved', 'insufficient-evidence', 'abandoned'] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

/** ADR-142 — an operator has to be able to ask "every capability gap, and what is
 *  each waiting on?" directly. Both kinds were always listed; there was no way to
 *  select one, and a gap read as a diagnosis whose symptom mentioned a missing
 *  capability. */
const KIND_FILTERS = ['all', 'capability_gap', 'diagnosis'] as const
type KindFilter = (typeof KIND_FILTERS)[number]

const KIND_LABEL: Record<string, string> = {
  capability_gap: 'capability gap',
  diagnosis: 'diagnosis',
}

/** ADR-142 — a capability gap's progress is a STAGE, not a round count.
 *
 *  `iterations` is ADR-082's diagnosis-round counter. Nothing increments it for a
 *  gap and nothing should: a gap does not iterate, it advances. Every row read
 *  "0/6 iterations" — including two cases that had been routed three times,
 *  produced agentcore#1630 and resumed on a real grant — because the page showed a
 *  diagnosis metric over data (steps_tried) that said exactly what had happened.
 */
function gapStage(it: DiagnosticInvestigation): { label: string; pr?: string } | null {
  if (it.kind !== 'capability_gap') return null
  const gap = it.gap as { invalid?: boolean } | null | undefined
  const steps = Array.isArray(it.steps_tried) ? it.steps_tried : []
  const kinds = steps.map(s => s.kind)

  if (gap?.invalid) {
    // A report that never landed. The repeats are steps on this one record.
    const n = 1 + kinds.filter(k => k === 'capability_gap_report_failed').length
    return { label: n > 1 ? `report failed ${n}×` : 'report failed' }
  }

  const proposal = steps.find(s => s.kind === 'capability_gap_proposal')
  const pr = proposal?.detail?.match(/pull\/(\d+)/)?.[0]
  const routed = kinds.filter(k => k === 'capability_gap_routed').length
  const blocked = kinds.includes('capability_gap_routing_blocked')
  const resumed = kinds.includes('resume_attempted')

  if (resumed) return { label: 'resumed — authoring', pr }
  if (proposal) return { label: 'proposed, awaiting merge', pr }
  if (blocked) return { label: 'not routed — nobody to build it' }
  if (routed > 1) return { label: `routed (attempt ${routed})` }
  if (routed === 1) return { label: 'routed to a builder' }
  return { label: 'filed' }
}

/**
 * Is this case carrying a live refusal (ADR-142 `wont_build`)?
 *
 * Read from the case's own steps with the same latest-decision-wins rule the server
 * applies, so a row cannot claim "refused" while filing disagrees — or stay silent
 * while filing turns every request away.
 */
function refusalOn(it: DiagnosticInvestigation): { by: string; reason: string } | null {
  const steps = Array.isArray(it.steps_tried) ? it.steps_tried : []
  let latest: InvestigationStep | null = null
  for (const s of steps) {
    if (s?.kind !== 'capability_gap_wont_build' && s?.kind !== 'capability_gap_wont_build_revoked') continue
    // >=, not >: these stamps have SECOND precision, so a refusal and the
    // revocation that lifts it moments later tie, and a strict compare keeps the
    // refusal — the row would read "won't build" for ever. steps_tried is
    // append-only, so the later entry is the later decision. Matches the server's
    // rule in capability_gap_refusal.go; if these two disagree the badge lies.
    if (!latest || (s.ts ?? '') >= (latest.ts ?? '')) latest = s
  }
  if (!latest || latest.kind !== 'capability_gap_wont_build') return null
  return { by: latest.by ?? 'an operator', reason: latest.detail ?? '' }
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(status)}`}>
      {status}
    </span>
  )
}

export function Investigations() {
  const [items, setItems] = useState<DiagnosticInvestigation[]>([])
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [kind, setKind] = useState<KindFilter>('all')
  // Terminal cases are collapsed by default. Five terminal reporting defects were
  // the loudest thing on this page while two live dependencies sat underneath them;
  // they are worth KEEPING (a closed case answers "why") and not worth showing.
  const [showStopped, setShowStopped] = useState(false)
  const [closing, setClosing] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (status: StatusFilter, k: KindFilter) => {
    try {
      const { investigations } = await api.getInvestigations({
        ...(status === 'all' ? {} : { status }),
        ...(k === 'all' ? {} : { kind: k }),
      })
      // Never assume a non-null array from the wire (console-nil-slice lesson).
      setItems(investigations ?? [])
      setError(null)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      setError(err instanceof Error ? err.message : 'Could not load investigations right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    void load(filter, kind)
    const t = setInterval(() => { void load(filter, kind) }, POLL_INTERVAL_MS)
    return () => clearInterval(t)
  }, [load, filter, kind])

  const awaitingApprovalCount = items.filter(it => it.status === 'awaiting-approval').length

  // ADR-142 usability: order by WHAT NEEDS YOU, not by updated_at.
  //
  // The list mixed three different things — a case waiting on the operator, a case
  // progressing on its own, and a case that has stopped — so the five loudest rows
  // were terminal reporting defects while two live dependencies sat underneath
  // them. Within a band the newest is still first.
  const BAND: Record<string, number> = {
    'awaiting-approval': 0, // needs you
    active: 1, 'awaiting-evidence': 1, // in flight
    resolved: 2, 'insufficient-evidence': 2, abandoned: 2, // stopped
  }
  const ordered = [...items].sort((a, b) => {
    const ba = BAND[a.status] ?? 1
    const bb = BAND[b.status] ?? 1
    if (ba !== bb) return ba - bb
    return (b.updated_at ?? '').localeCompare(a.updated_at ?? '')
  })
  const stoppedCount = ordered.filter(it => (BAND[it.status] ?? 1) === 2).length
  const refusedCount = ordered.filter(it => (BAND[it.status] ?? 1) === 2 && refusalOn(it) !== null).length
  const visible = showStopped ? ordered : ordered.filter(it => (BAND[it.status] ?? 1) !== 2)

  /** Close a case with a reason. fleet_owner only; the API refuses an empty reason. */
  const closeCase = async (it: DiagnosticInvestigation) => {
    const reason = window.prompt(
      `Close this case? A reason is recorded on it.\n\n${it.symptom.slice(0, 160)}`,
      it.kind === 'capability_gap' ? 'no longer needed — the capability landed' : 'no longer relevant',
    )
    if (reason == null || reason.trim() === '') return
    setClosing(it.id)
    try {
      await api.closeInvestigation(it.id, reason.trim())
      await load(filter, kind)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not close that case.')
    } finally {
      setClosing(null)
    }
  }

  /**
   * Refuse the CAPABILITY, not just this case.
   *
   * A plain close ends one row and nothing more: the next time an agent hits the same
   * wall, a new case is filed, a specialist is dispatched, a core PR is opened and
   * three reviewers read it — and the same decision gets made again. This records the
   * decision where filing consults it.
   *
   * The confirm spells that out, because "close" and "we are never building this" are
   * different acts and the second one binds future work.
   */
  const refuseCapability = async (it: DiagnosticInvestigation) => {
    const reason = window.prompt(
      'Refuse this CAPABILITY?\n\n' +
        'This closes the case AND records a standing decision not to build it, so the next ' +
        'time an agent asks, nothing is filed and no specialist is dispatched. You can lift ' +
        'it later with "allow again".\n\n' +
        `Capability: ${it.symptom.slice(0, 160)}\n\nReason (recorded):`,
      '',
    )
    if (reason == null || reason.trim() === '') return
    setClosing(it.id)
    try {
      await api.closeInvestigation(it.id, reason.trim(), 'wont_build')
      await load(filter, kind)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not refuse that capability.')
    } finally {
      setClosing(null)
    }
  }

  /** Lift a standing refusal. A "no" that cannot be lifted is worse than one repeated. */
  const allowAgain = async (it: DiagnosticInvestigation) => {
    const reason = window.prompt(
      'Allow this capability to be proposed again?\n\n' +
        'The case stays closed; what changes is that the next agent report opens a fresh ' +
        'one instead of being turned away.\n\nWhat changed (recorded):',
      '',
    )
    if (reason == null || reason.trim() === '') return
    setClosing(it.id)
    try {
      await api.allowCapabilityAgain(it.id, reason.trim())
      await load(filter, kind)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not lift that refusal.')
    } finally {
      setClosing(null)
    }
  }



  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500">
          Durable cases (ADR-082, ADR-142) — a diagnosis pursuing missing evidence, or a
          capability gap waiting for a capability to become usable. Every case is listed here,
          including reports that could not be filed, so nothing is in flight unseen.
        </p>
        <div className="flex gap-1 flex-wrap">
          {KIND_FILTERS.map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`px-2 py-1 rounded text-xs font-medium ${
                kind === k ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
            >
              {k === 'all' ? 'all kinds' : KIND_LABEL[k] ?? k}
            </button>
          ))}
          <span className="w-2" />
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2 py-1 rounded text-xs font-medium ${
                filter === s ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {awaitingApprovalCount > 0 && filter !== 'awaiting-approval' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {awaitingApprovalCount} case{awaitingApprovalCount > 1 ? 's' : ''} awaiting acquisition approval.
        </div>
      )}

      {stoppedCount > 0 && (
        <button
          onClick={() => setShowStopped(v => !v)}
          className="text-xs text-gray-500 hover:text-gray-800 underline"
        >
          {showStopped
            ? `hide ${stoppedCount} stopped case${stoppedCount > 1 ? 's' : ''}`
            : `${stoppedCount} stopped case${stoppedCount > 1 ? 's' : ''}` +
              (refusedCount > 0
                ? `, ${refusedCount} refused — show`
                : ' — show')}
        </button>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading && items.length === 0 && !error ? (
        <p className="text-sm text-gray-400 animate-pulse">Loading investigations...</p>
      ) : !error && items.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 px-5 py-10 text-center">
          <p className="text-sm text-gray-400">
            No investigations {filter !== 'all' ? `(${filter})` : 'yet'}.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {visible.map((it) => (
            <Link
              key={it.id}
              to={`/investigations/${encodeURIComponent(it.id)}`}
              className="flex items-start justify-between gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{it.symptom || '—'}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <StatusBadge status={it.status} />
                  {it.kind && it.kind !== 'diagnosis' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      {KIND_LABEL[it.kind] ?? it.kind}
                    </span>
                  )}
                  {waitingOn(it) && (
                    <span className="text-xs text-indigo-700 truncate max-w-md">{waitingOn(it)}</span>
                  )}
                  {it.current_intent && (
                    <span className="text-xs text-gray-500 truncate max-w-md">{it.current_intent}</span>
                  )}
                </div>
                {/* ADR-148 D2 — the change the OPERATOR owns, from the envelope's own
                    artifacts[]. Without this the row said a case was waiting and left the
                    one actionable fact on a detail page nobody had a reason to open. */}
                {operatorArtifacts(it).length > 0 && (
                  <p className="mt-1 text-xs text-amber-800">
                    <span className="font-medium">you own:</span>{' '}
                    {operatorArtifacts(it).map(a => a.path_or_thing).join(' · ')}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                {gapStage(it) ? (
                  <p className="text-xs text-gray-600">
                    {gapStage(it)!.label}
                    {gapStage(it)!.pr && (
                      <>
                        {' · '}
                        <span className="text-indigo-700">
                          {gapStage(it)!.pr!.replace(/^.*pull\//, '#')}
                        </span>
                      </>
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">{it.iterations}/{it.max_iterations} iterations</p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">updated {relativeTime(it.updated_at)}</p>
                {(BAND[it.status] ?? 1) !== 2 && (
                  <div className="mt-1 flex flex-col items-end gap-0.5">
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); void closeCase(it) }}
                      disabled={closing === it.id}
                      className="text-[10px] text-gray-400 hover:text-red-600 underline disabled:opacity-50"
                    >
                      {closing === it.id ? 'closing…' : 'close'}
                    </button>
                    {/* Only a capability gap has a capability to refuse; on a diagnosis
                        the server rejects it, so the control must not be offered. */}
                    {it.kind === 'capability_gap' && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); void refuseCapability(it) }}
                        disabled={closing === it.id}
                        className="text-[10px] text-gray-400 hover:text-red-600 underline disabled:opacity-50"
                        title="Close this case AND record a standing decision not to build the capability, so the next request is not routed"
                      >
                        won't build
                      </button>
                    )}
                  </div>
                )}
                {/* A refused capability says so, and offers the way back. Without this the
                    decision is invisible: the row reads as an ordinary abandoned case while
                    it is silently turning away every future request. */}
                {refusalOn(it) && (
                  <div className="mt-1 text-right">
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700"
                      title={`refused by ${refusalOn(it)!.by}: ${refusalOn(it)!.reason}`}
                    >
                      won't build
                    </span>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); void allowAgain(it) }}
                      disabled={closing === it.id}
                      className="block ml-auto mt-0.5 text-[10px] text-gray-400 hover:text-indigo-700 underline disabled:opacity-50"
                    >
                      allow again
                    </button>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
